import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchRecentAlarms } from '../services/api';
import { playAlarmSound } from '../utils/alarmSound';
import AlarmToastStack from '../components/alarm/AlarmToastStack';
import AlarmDrawers from '../components/alarm/AlarmDrawers';
import { severityKey, formatDateTime } from '../components/alarm/alarmFormat';

/* ===== 알람 알림 컨텍스트 =====
 * 최근 알람(/dashboard/alarms/recent)을 5초마다 한 번만 받아 와서
 *  - 헤더의 알람 티커·종 배지,
 *  - 새 알람 토스트 스택(화면 우상단),
 *  - 알림센터 목록 드로어 / 알람 상세 드로어
 * 가 같은 데이터를 쓰게 한다 (PNG 시안「헤더 및 팝업」).
 *
 * 확인(ACK)·음소거는 백엔드 API 가 없어 이 브라우저에만 저장한다(localStorage / sessionStorage). 백엔드 status 가
 * ACKNOWLEDGED/RESOLVED 인 알람은 확인된 것으로 본다. 심각도는 백엔드 ERROR → 화면 CRITICAL 로 보여 준다. */

const AlarmNotificationContext = createContext(null);

const ACK_STORAGE_KEY = 'smiAlarmAcked';          // 확인한 알람 키 목록 (최대 500)
const MUTE_STORAGE_KEY = 'smiAlarmMutes';         // { equipmentId: until(ms) }
const POLL_MS = 5000;
const FETCH_LIMIT = 100;

/* 백엔드 AlarmItem → 화면 공통 형태 */
const normalizeAlarm = (a) => {
  const at = a.createdAt || a.occurredAt || a.time || null;
  return {
    key: `${a.alarmId}_${a.alarmCode || a.code || ''}_${at || ''}`,
    alarmId: a.alarmId,
    equipmentId: a.equipmentId,
    equipmentName: a.equipmentName || (a.equipmentId != null ? `#${a.equipmentId}` : ''),
    factoryId: a.factoryId,
    factoryName: a.factoryName || '',
    code: a.alarmCode || a.code || '',
    type: a.alarmType || '',
    severity: severityKey(a.severity),
    message: a.message || '',
    status: a.status || 'ACTIVE',
    at,
    timeAgo: a.timeAgo || ''
  };
};

const readJson = (storage, key, fallback) => {
  try {
    const v = JSON.parse(storage.getItem(key));
    return v == null ? fallback : v;
  } catch (e) {
    return fallback;
  }
};

// 현재 경로 기반 필터 컨텍스트 결정 (토스트를 어느 알람까지 띄울지)
function getFilterContext(pathname, searchParams) {
  if (pathname.includes('/equipment-board')) {
    return { type: 'equipment-board' };
  }
  if (pathname === '/machine' || pathname.endsWith('/machine')) {
    const id = searchParams.get('id');
    return id ? { type: 'equipment', equipmentId: parseInt(id, 10) } : { type: 'all' };
  }
  if (pathname.includes('/factory') || pathname.includes('/factory-machine-overview') || pathname.includes('/realtime-shot-status')) {
    const fid = searchParams.get('factoryId');
    return fid ? { type: 'factory', factoryId: parseInt(fid, 10) } : { type: 'all' };
  }
  return { type: 'all' };
}

function filterAlarms(alarms, filterContext, equipmentBoardIds) {
  switch (filterContext.type) {
    case 'factory':
      return alarms.filter(a => a.factoryId === filterContext.factoryId);
    case 'equipment':
      return alarms.filter(a => a.equipmentId === filterContext.equipmentId);
    case 'equipment-board':
      if (equipmentBoardIds.length === 0) return alarms;
      return alarms.filter(a => equipmentBoardIds.includes(a.equipmentId));
    case 'all':
    default:
      return alarms;
  }
}

export function AlarmNotificationProvider({ children }) {
  const location = useLocation();
  const [summary, setSummary] = useState(null);          // 백엔드 AlarmSummaryResponse 원본(카운트)
  const [recentAlarms, setRecentAlarms] = useState([]);  // 정규화된 최근 알람 (최신순)
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);              // 새로 들어온 알람 (토스트)
  const [equipmentBoardIds, setEquipmentBoardIds] = useState([]);
  const [acked, setAcked] = useState(() => readJson(localStorage, ACK_STORAGE_KEY, []));
  const [mutes, setMutes] = useState(() => readJson(sessionStorage, MUTE_STORAGE_KEY, {}));
  const [drawer, setDrawer] = useState(null);            // null | { type: 'list' } | { type: 'detail', key }
  const knownKeysRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const mutesRef = useRef(mutes);
  useEffect(() => { mutesRef.current = mutes; }, [mutes]);

  const filterContext = getFilterContext(location.pathname, new URLSearchParams(location.search));

  const isMuted = useCallback((equipmentId) => {
    const until = mutesRef.current?.[String(equipmentId)];
    return typeof until === 'number' && until > Date.now();
  }, []);

  // 알람 폴링 (5초) — 헤더·드로어·토스트가 모두 이 결과를 쓴다
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await fetchRecentAlarms(FETCH_LIMIT);
        if (cancelled || !data) return;
        const list = (data.recentAlarms || []).map(normalizeAlarm);
        setSummary(data);
        setRecentAlarms(list);

        const fresh = [];
        list.forEach((alarm) => {
          if (knownKeysRef.current.has(alarm.key)) return;
          knownKeysRef.current.add(alarm.key);
          if (!isFirstLoadRef.current) fresh.push(alarm);
        });
        isFirstLoadRef.current = false;

        if (fresh.length > 0) {
          const visible = filterAlarms(fresh, filterContext, equipmentBoardIds).filter((a) => !isMuted(a.equipmentId));
          if (visible.length > 0) {
            playAlarmSound();
            setToasts(prev => [...visible, ...prev]);
          }
        }
      } catch (e) {
        // 네트워크 오류는 다음 폴링에서 복구된다
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [filterContext.type, filterContext.factoryId, filterContext.equipmentId, equipmentBoardIds, isMuted]);

  // 페이지 변경 시 known 알람은 유지하되 표시된 토스트는 초기화
  useEffect(() => {
    setToasts([]);
  }, [location.pathname]);

  const removeAlarm = useCallback((key) => {
    setToasts(prev => prev.filter(a => (a.key || a.id) !== key));
  }, []);

  const clearAllAlarms = useCallback(() => setToasts([]), []);

  const updateEquipmentBoardIds = useCallback((ids) => setEquipmentBoardIds(ids), []);

  /* ---- 확인(ACK) — 브라우저 저장 ---- */
  const ackedSet = useMemo(() => new Set(acked), [acked]);
  const isAcked = useCallback(
    (alarm) => alarm.status === 'ACKNOWLEDGED' || alarm.status === 'RESOLVED' || ackedSet.has(alarm.key),
    [ackedSet]
  );
  const acknowledge = useCallback((keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    setAcked((prev) => {
      const next = Array.from(new Set([...prev, ...list])).slice(-500);
      try { localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* 저장 실패는 무시 */ }
      return next;
    });
    setToasts(prev => prev.filter(a => !list.includes(a.key)));
  }, []);

  /* ---- 음소거 — 설비 단위, 이 세션에만 ---- */
  const muteEquipment = useCallback((equipmentId, minutes = 30) => {
    setMutes((prev) => {
      const next = { ...prev, [String(equipmentId)]: Date.now() + minutes * 60 * 1000 };
      try { sessionStorage.setItem(MUTE_STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* 저장 실패는 무시 */ }
      return next;
    });
    setToasts(prev => prev.filter(a => a.equipmentId !== equipmentId));
  }, []);
  const muteUntil = useCallback((equipmentId) => {
    const until = mutes?.[String(equipmentId)];
    return typeof until === 'number' && until > Date.now() ? until : null;
  }, [mutes]);

  /* ---- 드로어 ---- */
  const openList = useCallback(() => setDrawer({ type: 'list' }), []);
  const openDetail = useCallback((alarmOrKey) => setDrawer({ type: 'detail', key: typeof alarmOrKey === 'string' ? alarmOrKey : alarmOrKey?.key }), []);
  const closeDrawer = useCallback(() => setDrawer(null), []);

  const unackedAlarms = useMemo(() => recentAlarms.filter((a) => !isAcked(a)), [recentAlarms, isAcked]);
  const counts = useMemo(() => ({
    total: recentAlarms.length,
    unacked: unackedAlarms.length,
    critical: recentAlarms.filter((a) => a.severity === 'CRITICAL').length,
    unackedCritical: unackedAlarms.filter((a) => a.severity === 'CRITICAL').length,
    errorCount: summary?.errorCount ?? null,
    warningCount: summary?.warningCount ?? null,
    infoCount: summary?.infoCount ?? null,
    totalCount: summary?.totalCount ?? null
  }), [recentAlarms, unackedAlarms, summary]);

  const value = useMemo(() => ({
    /* 기존 소비자(설비현황판)용 — 토스트 목록 */
    alarms: toasts.map((a) => ({ ...a, id: a.key, time: formatDateTime(a.at) })),
    removeAlarm,
    clearAllAlarms,
    updateEquipmentBoardIds,
    filterContext,
    /* 헤더·드로어용 */
    loading,
    recentAlarms,
    unackedAlarms,
    counts,
    isAcked,
    acknowledge,
    muteEquipment,
    muteUntil,
    drawer,
    openList,
    openDetail,
    closeDrawer
  }), [toasts, removeAlarm, clearAllAlarms, updateEquipmentBoardIds, filterContext, loading, recentAlarms, unackedAlarms, counts,
    isAcked, acknowledge, muteEquipment, muteUntil, drawer, openList, openDetail, closeDrawer]);

  return (
    <AlarmNotificationContext.Provider value={value}>
      {children}
      {/* 설비현황판에서는 패널 내부에 표시하므로 글로벌 토스트 숨김 */}
      {filterContext.type !== 'equipment-board' && (
        <AlarmToastStack
          toasts={toasts}
          onClose={removeAlarm}
          onAck={(key) => acknowledge(key)}
          onOpenDetail={openDetail}
        />
      )}
      <AlarmDrawers
        drawer={drawer}
        recentAlarms={recentAlarms}
        counts={counts}
        isAcked={isAcked}
        acknowledge={acknowledge}
        muteEquipment={muteEquipment}
        muteUntil={muteUntil}
        onClose={closeDrawer}
        onOpenList={openList}
        onOpenDetail={openDetail}
      />
    </AlarmNotificationContext.Provider>
  );
}

export function useAlarmNotification() {
  const context = useContext(AlarmNotificationContext);
  if (!context) {
    throw new Error('useAlarmNotification must be used within AlarmNotificationProvider');
  }
  return context;
}

export default AlarmNotificationContext;
